/**
 * Routing settings tab — routing providers and Google Maps API key per tenant.
 *
 * @module panels/shuttle/settings
 */
import * as api from "../../api/shuttle.ts";
import type { TabContext } from "./index.ts";

const WALK_LABELS: Record<string, string> = {
	"": "Auto (Google if key set, else OSRM)",
	google: "Google Maps Routes API",
	osrm: "OSRM (self-hosted)",
	off: "Off (no walk legs)",
};

const DRIVE_LABELS: Record<string, string> = {
	"": "Auto (OSRM)",
	google: "Google Maps Routes API",
	osrm: "OSRM (self-hosted)",
};

const TILE_LABELS: Record<string, string> = {
	"": "Auto (Google if key set, else OSM)",
	google: "Google Maps Tiles",
	osm: "OpenStreetMap (free)",
};

function select(
	id: string,
	labels: Record<string, string>,
	current: string,
): string {
	return `<select id="${id}" class="shuttle-select">
        ${Object.entries(labels)
					.map(
						([v, l]) =>
							`<option value="${v}" ${current === v ? "selected" : ""}>${l}</option>`,
					)
					.join("")}
    </select>`;
}

export async function renderSettingsTab(
	root: HTMLElement,
	ctx: TabContext,
): Promise<void> {
	type Settings = {
		google_maps_api_key?: string | null;
		walk_routing_provider: string;
		drive_routing_provider: string;
		map_tile_provider: string;
		google_mode_enabled: boolean;
	};
	let settings: Settings | null = null;
	let loadErr = "";
	try {
		settings = (await api.getTenantSettings(ctx.tenantSlug)) as Settings;
	} catch (e) {
		loadErr = (e as Error).message;
	}

	const walkCurrent = settings?.walk_routing_provider ?? "";
	const driveCurrent = settings?.drive_routing_provider ?? "";
	const tileCurrent = settings?.map_tile_provider ?? "";
	const googleMode = settings?.google_mode_enabled ?? false;

	root.innerHTML = `
        <div class="panel-header"><h3>Routing settings</h3></div>

        ${loadErr ? `<p class="panel-error">${esc(loadErr)}</p>` : ""}

        <form id="settings-form" class="shuttle-form" style="max-width:520px" autocomplete="off">

            <label>Walk routing provider
                ${select("walk-provider", WALK_LABELS, walkCurrent)}
                <small class="panel-hint">
                    Controls which service calculates pedestrian walk legs in the journey planner.
                </small>
            </label>

            <label>Drive routing provider
                ${select("drive-provider", DRIVE_LABELS, driveCurrent)}
                <small class="panel-hint">
                    Controls which service calculates shuttle driving routes.
                    Google requires an API key below.
                </small>
            </label>

            <label>Map tile provider
                ${select("tile-provider", TILE_LABELS, tileCurrent)}
                <small class="panel-hint">
                    Google requires an API key below.
                    OpenStreetMap is free but requires attribution in the app.
                </small>
            </label>

            <label class="shuttle-checkbox-label" style="display:flex;align-items:flex-start;gap:.5em">
                <input id="google-mode" type="checkbox" ${googleMode ? "checked" : ""} style="margin-top:.25em" />
                <span>Enable Google mode (mobile app)
                    <small class="panel-hint" style="display:block">
                        App-only switch the mobile SDK reads to choose its map rendering mode.
                        Independent of the routing providers above.
                    </small>
                </span>
            </label>

            <label>Google Maps API key
                <input id="gmaps-key" type="password" class="settings-input"
                    placeholder="${settings?.google_maps_api_key ? "••••••••  (key set — paste to replace)" : "Paste key to set (leave blank to use global default)"}"
                    autocomplete="off" />
                <small class="panel-hint">
                    Applies to walk routing, drive routing, and map tiles for this tenant.
                    Leave blank to use the global <code>GOOGLE_MAPS_API_KEY</code> env var.
                </small>
            </label>

            <div id="settings-status" style="min-height:1.4em"></div>

            <div class="shuttle-form-actions">
                <button type="submit" id="settings-save" class="shuttle-btn primary">Save</button>
                ${
									settings?.google_maps_api_key
										? `<button type="button" id="settings-clear-key" class="shuttle-btn danger">Clear API key</button>`
										: ""
								}
            </div>
        </form>
    `;

	const status = root.querySelector("#settings-status") as HTMLElement;

	root
		.querySelector("#settings-form")
		?.addEventListener("submit", async (e) => {
			e.preventDefault();
			const walkProvider = (
				root.querySelector("#walk-provider") as HTMLSelectElement
			).value;
			const driveProvider = (
				root.querySelector("#drive-provider") as HTMLSelectElement
			).value;
			const tileProvider = (
				root.querySelector("#tile-provider") as HTMLSelectElement
			).value;
			const googleModeOn = (
				root.querySelector("#google-mode") as HTMLInputElement
			).checked;
			const keyInput = (
				root.querySelector("#gmaps-key") as HTMLInputElement
			).value.trim();

			const patch: api.RoutingSettingsPatch = {
				walk_routing_provider: walkProvider,
				drive_routing_provider: driveProvider,
				map_tile_provider: tileProvider,
				google_mode_enabled: googleModeOn,
			};
			if (keyInput !== "") {
				patch.google_maps_api_key = keyInput;
			}

			try {
				await api.updateRoutingSettings(ctx.tenantSlug, patch);
				status.textContent = "Saved.";
				status.className = "panel-hint";
			} catch (err) {
				status.textContent = (err as Error).message;
				status.className = "panel-error";
			}
		});

	root
		.querySelector("#settings-clear-key")
		?.addEventListener("click", async () => {
			if (
				!confirm(
					"Clear the Google Maps API key for this tenant? Routing and tiles will fall back to the global key.",
				)
			)
				return;
			try {
				await api.updateRoutingSettings(ctx.tenantSlug, {
					google_maps_api_key: "",
				});
				status.textContent = "API key cleared.";
				status.className = "panel-hint";
				await ctx.rerender(); // re-render here to hide the "Clear API key" button
			} catch (e) {
				status.textContent = (e as Error).message;
				status.className = "panel-error";
			}
		});
}

function esc(s: string): string {
	return s.replace(
		/[&<>"']/g,
		(c) =>
			({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
				c
			]!,
	);
}
